import { useCallback, useEffect, useRef, useState } from 'react';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { RowActionsMenu, type RowAction } from '../../job-roles/components/RowActionsMenu';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { DEDUCTION_CATEGORY_BLURBS, DEDUCTION_CATEGORY_CODES, DEDUCTION_CATEGORY_LABELS, type DeductionCategoryCode } from '../categories';
import { BulkAssignDialog } from '../components/BulkAssignDialog';
import { CatalogList, type CatalogRow } from '../components/CatalogList';
import { DefinitionFormDialog, type DefinitionDialogMode } from '../components/DefinitionFormDialog';
import { GarnishmentRecords } from '../components/GarnishmentRecords';
import { ListFilters, Pager, SortableHeader, StatusBadge, type FilterState } from '../components/ListControls';
import { PlanFormDialog } from '../components/PlanFormDialog';
import { GARNISHMENT_RULES_NOTE, NO_CALCULATION_NOTE } from '../copy';
import { formatDate, formatMoney } from '../dates';
import { isPercentType } from '../definitionForm';
import { deductionsGateway } from '../index';
import { useDebounced } from '../useDebounced';
import {
  deductionCalculationTypeLabels, deductionClassificationLabels, type BulkAssignmentResult, type DeductionCatalogItem, type DeductionDefinition, type Paged, type PlanSort,
} from '../types';
import { isOtherCatalogItem } from '../catalogData';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

type DefsState = { kind: 'loading' } | { kind: 'ready'; data: Paged<DeductionDefinition> } | { kind: 'forbidden' } | { kind: 'error'; message: string };
type CatalogState = { kind: 'loading' } | { kind: 'ready'; items: DeductionCatalogItem[] } | { kind: 'forbidden' } | { kind: 'error'; message: string };

type Dialog =
  | { kind: 'definition'; mode: DefinitionDialogMode; definitionId?: string; catalogType?: DeductionCatalogItem | null }
  | { kind: 'deactivate'; definition: DeductionDefinition }
  | { kind: 'assign'; definition: DeductionDefinition };

const EDITABLE_CATEGORIES = DEDUCTION_CATEGORY_CODES.filter((c) => c !== 'taxes-statutory');
const CATEGORY_OPTIONS = EDITABLE_CATEGORIES.map((c) => ({ value: c, label: DEDUCTION_CATEGORY_LABELS[c] }));

function catalogRow(item: DeductionCatalogItem): CatalogRow {
  const tags = [deductionClassificationLabels[item.classification].split(' (')[0]!];
  if (item.defaultCalculationType) tags.push(deductionCalculationTypeLabels[item.defaultCalculationType]);
  return { code: item.code, name: item.name, description: item.description, isHidden: item.isHidden, tags, opensCustomForm: isOtherCatalogItem(item) };
}

function amountText(def: DeductionDefinition): string {
  if (def.defaultAmount === null) return 'Set when assigned';
  if (def.calculationType && isPercentType(def.calculationType)) return `${def.defaultAmount}%`;
  return formatMoney(def.defaultAmount);
}

/** One page component for the five editable deduction category leaves (the statutory leaf is its own read-only page). The route decides the category. */
export function DeductionCategoryPage({ category }: { category: Exclude<DeductionCategoryCode, 'taxes-statutory'> }) {
  return <CategoryPage key={category} category={category} />;
}

/**
 * A People > Deductions category: the system catalog for the category (add from the catalog, prefilled, or add a custom deduction; hide or show a type for
 * this company) and the company's deduction definitions with search, filters, sortable columns, paging and row actions, including assigning a deduction to
 * employees (single, group, all eligible) through a preview-then-confirm flow. A definition whose tax treatment is not approved, or whose calculation is
 * not a fixed amount, can be saved but not activated, and the page says why. Garnishments and levies show the catalog and the existing records only:
 * "Garnishment limits and priorities are controlled by applicable rules", with rule review required. A 403 on a write turns the page read-only.
 */
function CategoryPage({ category }: { category: Exclude<DeductionCategoryCode, 'taxes-statutory'> }) {
  const label = DEDUCTION_CATEGORY_LABELS[category];
  const garnishment = category === 'garnishments-levies';
  const [filters, setFilters] = useState<FilterState>({ search: '', category, status: 'all', effectiveOn: '' });
  const search = useDebounced(filters.search.trim());
  const [sort, setSort] = useState<PlanSort>('name');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [defs, setDefs] = useState<DefsState>({ kind: 'loading' });
  const [catalog, setCatalog] = useState<CatalogState>({ kind: 'loading' });
  const [showHidden, setShowHidden] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [catalogNonce, setCatalogNonce] = useState(0);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [busyDef, setBusyDef] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setPage(1); }, [search, filters.category, filters.status, filters.effectiveOn]);

  useEffect(() => {
    const controller = new AbortController();
    deductionsGateway
      .listDefinitions({
        search: search || undefined, category: filters.category || undefined, status: filters.status, effectiveOn: filters.effectiveOn || undefined, sort, page, pageSize,
      }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setDefs({ kind: 'ready', data }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setDefs(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the deductions.', 'deduction') });
      });
    return () => controller.abort();
  }, [search, filters.category, filters.status, filters.effectiveOn, sort, page, pageSize, nonce]);

  useEffect(() => {
    const controller = new AbortController();
    deductionsGateway.listCatalog({ category, includeHidden: showHidden }, controller.signal)
      .then((items) => { if (!controller.signal.aborted) setCatalog({ kind: 'ready', items }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setCatalog(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the deduction types.', 'deduction') });
      });
    return () => controller.abort();
  }, [category, showHidden, catalogNonce]);

  const reload = useCallback(() => { setDefs((p) => (p.kind === 'ready' ? p : { kind: 'loading' })); setNonce((n) => n + 1); }, []);
  const reloadCatalog = useCallback(() => setCatalogNonce((n) => n + 1), []);
  const markReadOnly = useCallback(() => setReadOnly(true), []);

  const forbidden = defs.kind === 'forbidden' && catalog.kind === 'forbidden';
  const canWrite = !readOnly && !forbidden;
  // The category column only earns its width when the list spans categories.
  const allCategories = filters.category === '';
  const filtersActive = filters.search.trim() !== '' || filters.category !== category || filters.status !== 'all' || filters.effectiveOn !== '';

  function clearFilters() {
    setFilters({ search: '', category, status: 'all', effectiveOn: '' });
  }

  function saved(def: DeductionDefinition, mode: 'create' | 'edit') {
    setDialog(null);
    const reasons = def.configurationIssues.length > 0 && !def.isActive ? ` It is saved inactive: ${def.configurationIssues[0]}` : '';
    setNotice(mode === 'create' ? `${def.name} was added.${reasons}` : `${def.name} was saved. The change applies from now on.`);
    reload();
  }

  async function toggleHidden(row: CatalogRow) {
    setBusyCode(row.code);
    setActionError(null);
    setNotice(null);
    try {
      if (row.isHidden) await deductionsGateway.showCatalogType(row.code); else await deductionsGateway.hideCatalogType(row.code);
      setNotice(`${row.name} is now ${row.isHidden ? 'shown' : 'hidden'} for your company. Nothing was deleted.`);
      reloadCatalog();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setActionError(bdErrorMessage(err, 'Could not change this deduction type.', 'deduction'));
    } finally {
      setBusyCode(null);
    }
  }

  async function activate(def: DeductionDefinition) {
    setBusyDef(def.id);
    setActionError(null);
    setNotice(null);
    try {
      await deductionsGateway.setDefinitionActive(def, true);
      setNotice(`${def.name} is active. It can now be assigned to employees.`);
      reload();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setActionError(bdErrorMessage(err, 'Could not activate this deduction.', 'deduction'));
    } finally {
      setBusyDef(null);
    }
  }

  async function deactivate(def: DeductionDefinition) {
    setDeactivating(true);
    setDeactivateError(null);
    try {
      await deductionsGateway.setDefinitionActive(def, false);
      setDialog(null);
      setNotice(`${def.name} was deactivated. It was not deleted, and existing assignments are unchanged.`);
      reload();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setDeactivateError(bdErrorMessage(err, 'Could not deactivate this deduction.', 'deduction'));
    } finally {
      setDeactivating(false);
    }
  }

  function assigned(result: BulkAssignmentResult, def: DeductionDefinition) {
    setNotice(`${def.name} was ${result.replayed ? 'already assigned' : 'assigned'} to ${result.createdCount} ${result.createdCount === 1 ? 'employee' : 'employees'}.`);
    reload();
  }

  function actionsFor(def: DeductionDefinition): RowAction[] {
    const actions: RowAction[] = [{ key: 'view', label: 'View', onSelect: () => setDialog({ kind: 'definition', mode: 'view', definitionId: def.id }) }];
    if (canWrite) {
      actions.push({ key: 'edit', label: 'Edit', onSelect: () => setDialog({ kind: 'definition', mode: 'edit', definitionId: def.id }) });
      if (def.isActive) actions.push({ key: 'deactivate', label: 'Deactivate', danger: true, onSelect: () => { setDeactivateError(null); setDialog({ kind: 'deactivate', definition: def }); } });
      else if (!garnishment) actions.push({ key: 'activate', label: 'Activate', onSelect: () => void activate(def) });
      if (!garnishment) actions.push({ key: 'assign', label: 'Assign to employees', onSelect: () => setDialog({ kind: 'assign', definition: def }) });
    }
    return actions;
  }

  const data = defs.kind === 'ready' ? defs.data : null;
  const firstIndex = data ? (data.page - 1) * data.pageSize : 0;
  const catalogRows = catalog.kind === 'ready' ? catalog.items.map(catalogRow) : [];

  return (
    <section className="card card--wide jr-page bd-page">
      <div className="section-header">
        <h2 ref={headingRef} tabIndex={-1}>{label}</h2>
        {canWrite && (
          <div className="jr-actions">
            <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'definition', mode: 'create', catalogType: null })}>
              <span aria-hidden="true">+ </span>Add Custom Deduction
            </button>
          </div>
        )}
      </div>
      <p className="muted bd-lead">{DEDUCTION_CATEGORY_BLURBS[category]}</p>

      {garnishment && (
        <div className="bd-notice bd-notice--warn" role="note" data-testid="garnishment-note">
          <p><strong>Rule review required.</strong> {GARNISHMENT_RULES_NOTE}</p>
          <p>Phase 1 lists the types and the records already on file. EPN does not calculate garnishment limits or priorities here, and a garnishment definition cannot be activated until the applicable rules are reviewed.</p>
        </div>
      )}
      {!garnishment && <p className="bd-help">{NO_CALCULATION_NOTE}</p>}
      {readOnly && (
        <p className="jr-readonly" role="status" data-testid="read-only-notice">
          <span aria-hidden="true">{'🔒'} </span>You can view deductions but not change them. Ask a company administrator if you need to add, edit, activate or assign a deduction.
        </p>
      )}
      {notice && (
        <p className="jr-notice jr-notice--ok" role="status">
          <span aria-hidden="true">{'✓'} </span>{notice}{' '}
          <button type="button" className="link" onClick={() => { setNotice(null); headingRef.current?.focus(); }}>Dismiss</button>
        </p>
      )}
      {actionError && <p className="jr-error jr-error--block" role="alert">{actionError}</p>}

      {forbidden ? (
        <AccessDeniedNotice requiredPermissionLabel="permission to view deductions (deductioncodes.read)" />
      ) : (
        <>
          <h3>Deduction types in this category</h3>
          {catalog.kind === 'loading' && <p className="muted" role="status">Loading deduction types{'…'}</p>}
          {catalog.kind === 'error' && <RetryableError message={catalog.message} onRetry={reloadCatalog} />}
          {catalog.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view deductions (deductioncodes.read)" />}
          {catalog.kind === 'ready' && (
            <CatalogList
              idPrefix="bd-catalog" noun="deduction" rows={catalogRows} readOnly={!canWrite} busyCode={busyCode} showHidden={showHidden} onShowHidden={setShowHidden}
              emptyMessage={category === 'other-custom' ? 'This category has no starter types. Choose Add Custom Deduction to create your own.' : 'Every type in this category is hidden. Show a hidden type to add it.'}
              onAdd={(row) => setDialog({ kind: 'definition', mode: 'create', catalogType: row.opensCustomForm ? null : catalog.items.find((c) => c.code === row.code) ?? null })}
              onToggleHidden={(row) => void toggleHidden(row)}
            />
          )}

          <h3>Your deductions</h3>
          <ListFilters
            idPrefix="bd-defs" noun="deductions" state={filters} categories={CATEGORY_OPTIONS} active={filtersActive} onClear={clearFilters}
            onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          />
          {defs.kind === 'loading' && <p className="muted" role="status">Loading deductions{'…'}</p>}
          {defs.kind === 'error' && <RetryableError message={defs.message} onRetry={reload} />}
          {defs.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view deductions (deductioncodes.read)" />}

          {data && data.totalCount === 0 && !filtersActive && (
            <EmptyState
              message={`No ${label} deductions yet. Add one from the types above, or add a custom deduction.`}
              action={canWrite ? <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'definition', mode: 'create', catalogType: null })}>Add Custom Deduction</button> : undefined}
            />
          )}
          {data && data.totalCount === 0 && filtersActive && (
            <EmptyState message="No deductions match your search or filters." action={<button type="button" className="jr-btn jr-btn--secondary" onClick={clearFilters}>Clear filters</button>} />
          )}

          {data && data.totalCount > 0 && (
            <>
              <p className="muted" aria-live="polite" data-testid="defs-count">
                Showing {firstIndex + 1}{'–'}{firstIndex + data.items.length} of {data.totalCount} {data.totalCount === 1 ? 'deduction' : 'deductions'}
              </p>
              <div className="jr-table-scroll" role="region" aria-label="Deductions table" tabIndex={0}>
                <table className="jr-table jr-table--actions bd-table">
                  <caption className="jr-sr-only">Deductions for this company</caption>
                  <thead>
                    <tr>
                      <SortableHeader label="Name" ascValue="name" descValue="-name" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                      {allCategories && <SortableHeader label="Category" ascValue="category" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />}
                      <th scope="col">Calculation</th>
                      <th scope="col">Amount</th>
                      <SortableHeader label="Effective" ascValue="effectiveStart" descValue="-effectiveStart" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                      <SortableHeader label="Status" ascValue="status" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((def) => {
                      const dash = <span className="muted" title="Not set">{'—'}</span>;
                      return (
                        <tr key={def.id} aria-busy={busyDef === def.id || undefined}>
                          <th scope="row" className="jr-table__title">
                            <button type="button" className="link" onClick={() => setDialog({ kind: 'definition', mode: 'view', definitionId: def.id })}>{def.name}</button>
                            <span className="bd-table-note">{def.shortCode ?? 'No short code'} · {def.isCustom ? 'Custom' : 'Catalog'}</span>
                          </th>
                          {allCategories && <td>{def.categoryCode ? DEDUCTION_CATEGORY_LABELS[def.categoryCode] : dash}</td>}
                          <td>{def.calculationType ? deductionCalculationTypeLabels[def.calculationType] : def.amountType === 2 ? 'Percentage' : 'Flat amount'}</td>
                          <td>{amountText(def)}</td>
                          <td className="jr-nowrap">{def.effectiveStartDate ? formatDate(def.effectiveStartDate) : dash}{def.effectiveEndDate ? ` to ${formatDate(def.effectiveEndDate)}` : ''}</td>
                          <td><StatusBadge isActive={def.isActive} configurationStatus={def.configurationStatus} /></td>
                          <td><RowActionsMenu label={`Actions for ${def.name}`} actions={actionsFor(def)} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager page={data.page} pageSize={data.pageSize} totalCount={data.totalCount} onPage={setPage} onPageSize={(n) => { setPageSize(n); setPage(1); }} />
            </>
          )}

          {garnishment && <GarnishmentRecords />}
        </>
      )}

      {dialog?.kind === 'definition' && (
        <DefinitionFormDialog
          mode={dialog.mode}
          category={category}
          {...(dialog.definitionId ? { definitionId: dialog.definitionId } : {})}
          catalogType={dialog.catalogType ?? null}
          onClose={() => setDialog(null)}
          onSaved={saved}
          onForbidden={markReadOnly}
          renderCreateBenefit={({ onCreated, onClose }) => (
            <PlanFormDialog mode="create" category="health-wellness" categoryChoices={['health-wellness', 'insurance', 'retirement-savings', 'financial-equity', 'family-lifestyle', 'other-custom']} onClose={onClose} onSaved={(plan) => onCreated(plan)} onForbidden={markReadOnly} />
          )}
        />
      )}
      {dialog?.kind === 'deactivate' && (
        <ConfirmDialog
          title="Deactivate this deduction?" confirmLabel="Deactivate" busy={deactivating} error={deactivateError}
          onCancel={() => setDialog(null)} onConfirm={() => void deactivate(dialog.definition)}
        >
          <p className="bd-modal-note">
            Deactivating {dialog.definition.name} keeps it in the list and keeps existing assignments, but it cannot be assigned to anyone new. A deduction is never deleted from this page.
          </p>
        </ConfirmDialog>
      )}
      {dialog?.kind === 'assign' && (
        <BulkAssignDialog definition={dialog.definition} onClose={() => setDialog(null)} onDone={assigned} onForbidden={markReadOnly} />
      )}
    </section>
  );
}

